/**
 * Coordinates direct uploads and new versions.
 *
 * Both workflows use presigned content transfer but create different version
 * metadata. Connector-owned workspace intake is Jobs-only and never shares this
 * browser upload boundary.
 */
import { useCallback, useRef } from 'react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  uploadFile,
  uploadFileVersion,
} from '../services/vault-api'
import type { ItemResponse } from '../types'

interface VaultUploadOptions {
  api: NodeApiClient
  orgId: string
  vaultId: string
  folderId: string
  refresh: () => Promise<void>
  run: (action: () => Promise<unknown>, success: string) => Promise<boolean>
  closeVersionHistory: () => void
}

export const useVaultUploads = ({
  api,
  orgId,
  vaultId,
  folderId,
  refresh,
  run,
  closeVersionHistory,
}: VaultUploadOptions) => {
  const fileInput = useRef<HTMLInputElement>(null)
  const versionInput = useRef<HTMLInputElement>(null)
  const versionTarget = useRef<ItemResponse | null>(null)

  const uploadFiles = useCallback((files: File[], parentId: string) => {
    const batch = async () => {
      const results = []
      for (const file of files) {
        try {
          const value = await uploadFile(
            api, orgId, vaultId, parentId, file,
          )
          results.push({ status: 'fulfilled' as const, value })
        } catch (reason) {
          results.push({ status: 'rejected' as const, reason })
        }
      }
      await refresh()
      const failed = results.filter(result => result.status === 'rejected')
      if (failed.length) throw new Error(`${failed.length} of ${files.length} uploads failed`)
      return files.length
    }
    toast.promise(batch(), {
      loading: files.length === 1 ? `Uploading “${files[0].name}”…` : `Uploading ${files.length} files…`,
      success: count => count === 1 ? 'File uploaded' : `${count} files uploaded`,
      error: (error: Error) => error.message,
    })
  }, [api, orgId, refresh, vaultId])

  const chooseVersion = (entry: ItemResponse) => {
    closeVersionHistory()
    versionTarget.current = entry
    versionInput.current?.click()
  }

  const inputs = (
    <>
      <input
        ref={fileInput}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          if (files.length) uploadFiles(files, folderId)
          event.target.value = ''
        }}
      />
      <input
        ref={versionInput}
        type="file"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          const target = versionTarget.current
          if (file && target) {
            void run(
              () => uploadFileVersion(api, orgId, vaultId, target.item.itemId, file),
              'New version uploaded',
            )
          }
          event.target.value = ''
          versionTarget.current = null
        }}
      />
    </>
  )

  return { chooseVersion, fileInput, inputs, uploadFiles }
}
